import { useEffect, useMemo, useRef, useState } from "react";
import { EGGER_DECOR_GROUPS, type EggerDecorGroup } from "../domain/eggerDecors";
import { calculatePrice, formatPrice, priceIsComplete } from "../domain/pricing";
import { priceKeys, priceLineTitle, priceSections, validPriceValue, type PriceLine, type PriceSection, type PriceValues } from "../domain/priceList";
import { usePlannerStore } from "../store/usePlannerStore";
import { usePriceStore } from "../store/usePriceStore";
import { initialProject } from "../domain/project";
import type { PlannerProject, WardrobeModule } from "../types";
import { api } from "./workspaceApi";
import "./PriceEditor.css";

type HistoryRow = { key: string; old_value: number | null; new_value: number | null; changed_at: string; changed_by: string | null };
export type AdminPrices = {
  values: Record<string, number>;
  confirmed: Record<string, boolean>;
  updatedAt: string | null;
  history: HistoryRow[];
  changed?: number;
};
type LineState = "ok" | "missing" | "unchecked" | "changed" | "invalid";

const LINES = () => priceSections().flatMap((section) => section.lines);
/** Сколько строк требуют внимания: цена не задана или не проверена. */
export const priceAttentionCount = (data: Pick<AdminPrices, "values" | "confirmed">) =>
  LINES().filter((line) => data.values[line.key] === undefined || !data.confirmed[line.key]).length;

const numberText = (value: number | undefined) => value === undefined ? "" : String(value).replace(".", ",");
const parse = (text: string): number | null | "invalid" => {
  const trimmed = text.trim().replace(/[\s ]/g, "").replace(",", ".");
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : "invalid";
};
const amount = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value);
const when = (value: string) =>
  new Date(value).toLocaleString("ru-RU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const DECOR_GROUPS = Object.keys(EGGER_DECOR_GROUPS) as EggerDecorGroup[];

/** Изделия-примеры: на них видно, как правка прайса меняет смету. */
const sample = (modules: WardrobeModule[]): PlannerProject => ({ ...structuredClone(initialProject), modules });
const SAMPLES: Array<{ label: string; project: PlannerProject }> = [
  { label: "Шкаф 1200 × 2400, две двери", project: sample([{ id: "a", type: "double", width: 1200, height: 2400, depth: 600, door: "hinged",
    facade: "wood", handle: "bar", baseType: "plinth", baseHeight: 72,
    items: [{ id: "s1", type: "shelf", y: 1900 }, { id: "s2", type: "shelf", y: 1500 }, { id: "r", type: "rail", y: 1700 }] }]) },
  { label: "Шкаф-купе 1800 × 2400", project: sample([{ id: "b", type: "double", width: 1800, height: 2400, depth: 600, door: "sliding",
    facade: "wood", handle: "bar", baseType: "plinth", baseHeight: 72, items: [{ id: "r", type: "rail", y: 1700 }] }]) },
  { label: "Стеллаж 1600 × 2000, 4 × 5", project: sample([{ id: "c", type: "shelving", width: 1600, height: 2000, depth: 350, door: "open",
    facade: "wood", handle: "bar", items: [], shelvingColumns: 4, shelvingRows: 5, shelvingBase: "plinth" }]) }
];

/**
 * Прайс конструктора — единственное место, где задаются цены. Пустое поле —
 * цены нет: клиент видит «стоимость уточняется». Строки первичного заполнения
 * не отмечены как проверенные, пока администратор их не изменит или не отметит.
 */
export function PriceEditor({ busy, run, notify, onDirtyChange, onAttentionChange }: {
  busy: boolean;
  run: (action: () => Promise<void>) => Promise<void>;
  notify: (message: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onAttentionChange?: (count: number) => void;
}) {
  const sections = useMemo(() => priceSections(), []);
  const lines = useMemo(() => sections.flatMap((section) => section.lines), [sections]);
  const [data, setData] = useState<AdminPrices | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<Set<string>>(new Set());
  /** Строки, с которых сняли сохранённую отметку «проверена» (12.10). */
  const [unconfirm, setUnconfirm] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [onlyAttention, setOnlyAttention] = useState(false);
  const setPublicPrices = usePriceStore((state) => state.setValues);
  const currentProject = usePlannerStore((state) => state.project);
  const content = useRef<HTMLDivElement>(null);

  const apply = (next: AdminPrices) => {
    setData(next);
    setDraft(Object.fromEntries(lines.map((line) => [line.key, numberText(next.values[line.key])])));
    setConfirm(new Set());
    setUnconfirm(new Set());
    setPublicPrices(next.values, next.updatedAt);
    onAttentionChange?.(priceAttentionCount(next));
  };
  useEffect(() => { void run(async () => apply(await api<AdminPrices>("/prices"))); }, []);

  const stateOf = (line: PriceLine): LineState => {
    const parsed = parse(draft[line.key] ?? "");
    const saved = data?.values[line.key];
    if (parsed === "invalid" || (parsed !== null && !validPriceValue(line, parsed))) return "invalid";
    if ((parsed ?? undefined) !== saved) return "changed";
    if (saved === undefined) return "missing";
    if ((!data?.confirmed[line.key] || unconfirm.has(line.key)) && !confirm.has(line.key)) return "unchecked";
    return "ok";
  };
  const states = new Map(lines.map((line) => [line.key, stateOf(line)]));
  const count = (state: LineState) => [...states.values()].filter((value) => value === state).length;
  const changed = count("changed");
  const invalid = count("invalid");
  const missing = count("missing");
  const unchecked = count("unchecked");
  const dirty = changed > 0 || confirm.size > 0 || unconfirm.size > 0;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);

  // Черновик прайса — для предпросмотра сметы до сохранения.
  const draftValues = useMemo<PriceValues>(() => {
    const values: Record<string, number> = {};
    lines.forEach((line) => {
      const parsed = parse(draft[line.key] ?? "");
      if (typeof parsed === "number" && validPriceValue(line, parsed)) values[line.key] = parsed;
    });
    return values;
  }, [draft, lines]);

  const matches = (line: PriceLine, section: PriceSection) => {
    const text = query.trim().toLowerCase();
    if (onlyAttention && states.get(line.key) === "ok") return false;
    return !text || `${section.title} ${line.label}`.toLowerCase().includes(text);
  };
  const sectionCounts = (section: PriceSection) => ({
    attention: section.lines.filter((line) => ["missing", "unchecked", "invalid"].includes(states.get(line.key)!)).length,
    changed: section.lines.filter((line) => states.get(line.key) === "changed").length
  });

  const save = () => void run(async () => {
    if (invalid) throw new Error("Исправьте цены, отмеченные красным: нужно число не меньше нуля");
    const values: Record<string, number | null> = {};
    lines.forEach((line) => {
      const parsed = parse(draft[line.key] ?? "");
      if (parsed !== "invalid" && (parsed ?? undefined) !== data?.values[line.key]) values[line.key] = parsed;
    });
    const result = await api<AdminPrices>("/prices", "PUT", { values, confirm: [...confirm], unconfirm: [...unconfirm] });
    apply(result);
    notify(result.changed ? `Прайс сохранён: изменено позиций — ${result.changed}. Смета у клиентов пересчитана.` : "Отметки о проверке сохранены");
  });
  const toggle = (key: string) => (current: Set<string>) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  };
  // Сохранённую отметку можно снять: строка вернётся в «не проверено» после «Сохранить».
  const toggleConfirm = (key: string) => data?.confirmed[key] ? setUnconfirm(toggle(key)) : setConfirm(toggle(key));
  const confirmAll = () => {
    setUnconfirm(new Set());
    setConfirm((current) => new Set([...current,
      ...lines.filter((line) => states.get(line.key) === "unchecked" && !data?.confirmed[line.key]).map((line) => line.key)]));
  };
  const jump = (id: string) => content.current?.querySelector(`#price-section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (!data) return <div className="pe-loading">Загружаю прайс…</div>;

  const lineByKey = new Map(lines.map((line) => [line.key, line]));
  const field = (line: PriceLine, label?: string) => {
    const state = states.get(line.key)!;
    const saved = data.values[line.key];
    const savedChecked = Boolean(data.confirmed[line.key]);
    const checked = (savedChecked && !unconfirm.has(line.key)) || confirm.has(line.key) || state === "changed";
    return <div className={`pe-field is-${state}`}>
      <div className="pe-field__input">
        <input id={`price-${line.key}`} inputMode="decimal" value={draft[line.key] ?? ""} placeholder="не задана"
          aria-label={label ?? priceLineTitle(line.key)} aria-invalid={state === "invalid"}
          onChange={(event) => setDraft((current) => ({ ...current, [line.key]: event.target.value }))} />
        <span>{line.unit}</span>
      </div>
      <div className="pe-field__status">
        {state === "changed" ? <span className="pe-was" title="Сохранённая цена">было {amount(saved ?? null)}</span>
          : state === "missing" ? <span className="pe-flag">не задана</span>
            : state === "invalid" ? <span className="pe-flag">нужно число ≥ 0</span>
              : <label className="pe-check" title={savedChecked
                  ? "Цена проверена. Снимите галочку и нажмите «Сохранить», чтобы вернуть строку в «не проверено»"
                  : "Отметить цену как проверенную"}>
                  <input type="checkbox" checked={checked} disabled={busy}
                    onChange={() => toggleConfirm(line.key)} />
                  <span>{checked ? "проверена" : "проверить"}</span>
                </label>}
      </div>
    </div>;
  };

  const boardSection = sections.find((section) => section.id === "board")!;
  const backSection = sections.find((section) => section.id === "back")!;
  const matrixRows = DECOR_GROUPS.filter((group) =>
    matches(lineByKey.get(priceKeys.board(group))!, boardSection) || matches(lineByKey.get(priceKeys.back(group))!, backSection));
  const otherSections = sections.filter((section) => section.id !== "board" && section.id !== "back");
  const visibleHistory = data.history.filter((row) =>
    !query.trim() || priceLineTitle(row.key).toLowerCase().includes(query.trim().toLowerCase()));

  return <div className="pe">
    <div className="pe-toolbar">
      <div className="pe-stats" aria-label="Состояние прайса">
        <span><b>{lines.length}</b> позиций</span>
        <button type="button" className={`is-missing${missing ? "" : " is-zero"}`} onClick={() => setOnlyAttention(true)}><b>{missing}</b> не задано</button>
        <button type="button" className={`is-unchecked${unchecked ? "" : " is-zero"}`} onClick={() => setOnlyAttention(true)}><b>{unchecked}</b> не проверено</button>
        {changed > 0 && <span className="is-changed"><b>{changed}</b> изменено</span>}
        {data.updatedAt && <span className="pe-updated">обновлён {when(data.updatedAt)}</span>}
      </div>
      <div className="pe-tools">
        <input type="search" className="pe-search" placeholder="Найти позицию…" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Поиск по прайсу" />
        <label className="pe-filter"><input type="checkbox" checked={onlyAttention} onChange={(event) => setOnlyAttention(event.target.checked)} /> Только требующие внимания</label>
      </div>
      <div className="pe-actions">
        {unchecked > 0 && <button type="button" disabled={busy}
          onClick={confirmAll}>Отметить все проверенными</button>}
        <button type="button" disabled={busy || !dirty} onClick={() => apply(data)}>Отменить</button>
        <button type="button" className="workspace-primary" disabled={busy || invalid > 0 || !dirty} onClick={save}>
          {changed ? `Сохранить · ${changed}` : "Сохранить"}
        </button>
      </div>
    </div>

    <div className="pe-layout">
      <nav className="pe-sections" aria-label="Разделы прайса">
        {[{ id: "board", title: "Плита и задник", lines: [...boardSection.lines, ...backSection.lines] } as PriceSection, ...otherSections].map((section) => {
          const counts = sectionCounts(section);
          return <button type="button" key={section.id} onClick={() => jump(section.id)}>
            <span>{section.title}</span>
            {counts.changed > 0 ? <small className="is-changed">{counts.changed}</small>
              : counts.attention > 0 ? <small className="is-attention">{counts.attention}</small> : null}
          </button>;
        })}
        <button type="button" onClick={() => jump("history")}><span>Журнал</span>{data.history.length > 0 && <small>{data.history.length}</small>}</button>
      </nav>

      <div className="pe-content" ref={content}>
        {matrixRows.length > 0 && <section className="pe-card" id="price-section-board">
          <header><h3>Плита ЛДСП 16 мм и задняя стенка 8–10 мм</h3><p>По группе декора Egger. Фасад ЛДСП считается по цене плиты своего декора.</p></header>
          <table className="pe-table pe-matrix">
            <thead><tr><th>Группа декора</th><th>Плита ЛДСП 16 мм</th><th>Задняя стенка 8–10 мм</th></tr></thead>
            <tbody>{matrixRows.map((group) => <tr key={group}>
              <th scope="row">{EGGER_DECOR_GROUPS[group]}</th>
              <td data-label="Плита ЛДСП 16 мм">{field(lineByKey.get(priceKeys.board(group))!, `Плита ЛДСП: ${EGGER_DECOR_GROUPS[group]}`)}</td>
              <td data-label="Задняя стенка 8–10 мм">{field(lineByKey.get(priceKeys.back(group))!, `Задняя стенка: ${EGGER_DECOR_GROUPS[group]}`)}</td>
            </tr>)}</tbody>
          </table>
        </section>}

        <div className="pe-grid">
          {otherSections.map((section) => {
            const rows = section.lines.filter((line) => matches(line, section));
            if (!rows.length) return null;
            return <section className="pe-card" id={`price-section-${section.id}`} key={section.id}>
              <header><h3>{section.title}</h3>{section.hint && <p>{section.hint}</p>}</header>
              <table className="pe-table">
                <tbody>{rows.map((line) => <tr key={line.key}>
                  <th scope="row">{line.label}</th>
                  <td>{field(line)}</td>
                </tr>)}</tbody>
              </table>
            </section>;
          })}
        </div>

        <section className="pe-card pe-history" id="price-section-history">
          <header><h3>Журнал изменений</h3><p>Последние 60 изменений: кто, когда, было → стало.</p></header>
          {visibleHistory.length ? <table className="pe-table pe-log">
            <thead><tr><th>Когда</th><th>Кто</th><th>Позиция</th><th>Было</th><th>Стало</th></tr></thead>
            <tbody>{visibleHistory.map((row, index) => <tr key={`${row.changed_at}-${row.key}-${index}`}>
              <td>{when(row.changed_at)}</td><td>{row.changed_by ?? "—"}</td><td>{priceLineTitle(row.key)}</td>
              <td className="is-number">{amount(row.old_value)}</td><td className="is-number">{amount(row.new_value)}</td>
            </tr>)}</tbody>
          </table> : <p className="pe-empty">Изменений пока не было.</p>}
        </section>
      </div>

      <aside className="pe-preview" aria-label="Как изменится смета">
        <h3>Как изменится смета</h3>
        <p>{changed ? "С учётом несохранённых правок" : "По текущему прайсу"}</p>
        {[...(currentProject.modules.length ? [{ label: "Проект в конструкторе", project: currentProject }] : []), ...SAMPLES].map((item) => {
          const before = calculatePrice(item.project, data.values);
          const after = calculatePrice(item.project, draftValues);
          const delta = priceIsComplete(before) && priceIsComplete(after) ? after.total - before.total : 0;
          return <div className="pe-sample" key={item.label}>
            <span>{item.label}</span>
            <strong>{priceIsComplete(after) ? `${formatPrice(after.total)} ₽` : "уточняется"}</strong>
            {delta !== 0 && <small className={delta > 0 ? "is-up" : "is-down"}>
              {delta > 0 ? "+" : "−"}{formatPrice(Math.abs(delta))} ₽ · было {formatPrice(before.total)} ₽</small>}
            {!priceIsComplete(after) && <small className="is-warn">нет цены: {after.missing.slice(0, 2).join("; ")}{after.missing.length > 2 ? "…" : ""}</small>}
          </div>;
        })}
      </aside>
    </div>
  </div>;
}
